// API Service Layer - Centralized API functions
// Uses existing getCookie pattern for CSRF tokens and session authentication

// Utility function to get CSRF token from cookies
const getCookie = (name) => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Base API configuration
const API_BASE_URL = '/api';

// Common fetch configuration for authenticated requests
const createAuthenticatedRequest = (method = 'GET', body = null) => {
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken'),
    },
    credentials: 'include', // Include cookies for session authentication
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  return config;
};

// API Functions

/**
 * Get movie recommendations for the current user
 * @returns {Promise<Object>} Response with titles array and count
 */
export const getRecommendations = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/recommendations/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    throw error;
  }
};

/**
 * Get user's watchlist/favorites
 * @returns {Promise<Object>} Response with titles array and count
 */
export const getFavorites = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/favorites/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching favorites:', error);
    throw error;
  }
};

/**
 * Get user's watched movies
 * @returns {Promise<Object>} Response with titles array and count
 */
export const getWatched = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/watched/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching watched movies:', error);
    throw error;
  }
};

/**
 * Update user's preference for a movie
 * @param {string} imdb_id - The IMDB ID of the movie
 * @param {number} preference - The preference value (1=watchlist, 2=seen, 3=not interested, 4=liked, 5=disliked)
 * @returns {Promise<Object>} Response with Code and Data
 */
export const updatePreference = async (imdb_id, preference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/title-preferences/`, 
      createAuthenticatedRequest('POST', {
        imdb_id,
        preference
      })
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating preference:', error);
    throw error;
  }
};

/**
 * Get user information
 * @returns {Promise<Object>} User data
 */
export const getUserInfo = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/user-info/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    try {
      window.localStorage.setItem('picklUser', JSON.stringify(data));
    } catch (e) {
      // ignore storage errors
    }
    return data;
  } catch (error) {
    console.error('Error fetching user info:', error);
    throw error;
  }
};

/**
 * Get user questionnaire data (age and gender)
 * @returns {Promise<Object>} User questionnaire data
 */
export const getUserQuestionnaireData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/user-questionnaire-data/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('getUserQuestionnaireData response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching user questionnaire data:', error);
    throw error;
  }
};

/**
 * Update user questionnaire data (age and gender)
 * @param {Object} questionnaireData - Object containing age and/or gender
 * @returns {Promise<Object>} Update response
 */
export const updateUserQuestionnaireData = async (questionnaireData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update-user-questionnaire/`, 
      createAuthenticatedRequest('POST', questionnaireData)
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user questionnaire data:', error);
    throw error;
  }
};

/**
 * Update user email address
 * @param {string} email - New email address
 * @returns {Promise<Object>} Update response
 */
export const updateUserEmail = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update-user-email/`, 
      createAuthenticatedRequest('POST', { email })
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user email:', error);
    throw error;
  }
};

/**
 * Update user password
 * @param {Object} passwordData - Object containing old_password, new_password, and confirm_password
 * @returns {Promise<Object>} Update response
 */
export const updateUserPassword = async (passwordData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update-user-password/`, 
      createAuthenticatedRequest('POST', passwordData)
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user password:', error);
    throw error;
  }
};

/**
 * Update user information (username and/or email)
 * @param {Object} userData - Object containing username and/or email
 * @returns {Promise<Object>} Update response
 */
export const updateUserInfo = async (userData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update-user-info/`, 
      createAuthenticatedRequest('POST', userData)
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user info:', error);
    throw error;
  }
};

/**
 * Get CSRF token from server
 * @returns {Promise<string>} CSRF token
 */
export const getCSRFToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/csrf-token/`, 
      createAuthenticatedRequest('GET')
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
};

// Error handling utilities
export const handleAPIError = (error) => {
  if (error.message.includes('401') || error.message.includes('403')) {
    // Redirect to login if unauthorized or forbidden
    // Note: This will be handled by the calling component using React Router
    console.error('Authentication required');
  } else {
    // Handle other errors
    console.error('API Error:', error);
  }
}; 